import {Controller, useForm} from "react-hook-form";
import {yupResolver} from "@hookform/resolvers/yup";
import {Breadcrumb, Input} from "antd";
import {Link} from "react-router-dom";
import {
  useConnectTiktok,
  useDisconnectTiktok,
  useGetIntegrationsStatus,
} from "../../../../../react-query-hooks/integrations/integrations";
import yup from "../../../../../utils/yup-extended";
import {DashboardWrapper} from "../../../../../layouts/dashboard-wrapper";
import {StyledPageHeader} from "../../../../../styles/dashboard/calls/calls";
import {
  StyledAutomationsPageContent,
  StyledIntegrationConnectButton,
  StyledIntegrationConnectPanel,
  StyledIntegrationDetailCard,
  StyledIntegrationDetailHeader,
  StyledIntegrationDetailLayout,
  StyledIntegrationField,
} from "../../automations.style";
import {IntegrationConnectionVisual} from "../components/IntegrationConnectionVisual";
import {TiktokIcon} from "../../icons/tiktok-icon";
import {FormFieldError} from "../../../../../design-system/form-field";

const TRACKING_POINTS = [
  "Public profile views",
  "Initiate checkout events",
  "Successful purchase",
];

const PIXEL_ID_HELP_URL =
  "https://ads.tiktok.com/help/article/get-started-pixel-standard-mode";
const EVENTS_API_HELP_URL =
  "https://business-api.tiktok.com/portal/docs?id=1771101027431425";

type TiktokPixelFormValues = {
  tiktokPixelId: string;
  eventsApiAccessToken?: string;
};

const tiktokPixelSchema = yup.object().shape({
  tiktokPixelId: yup
    .string()
    .trim()
    .required("TikTok Pixel ID is required")
    .typeError("Must be a valid Pixel ID"),
  eventsApiAccessToken: yup.string().trim().notRequired(),
});

export function TiktokPixelIntegrationPage() {
  const {control, handleSubmit, reset} = useForm<TiktokPixelFormValues>({
    resolver: yupResolver(tiktokPixelSchema),
    mode: "onSubmit",
    defaultValues: {tiktokPixelId: "", eventsApiAccessToken: ""},
  });

  const integrationsStatusQuery = useGetIntegrationsStatus();
  const connectTiktokMutation = useConnectTiktok();
  const disconnectTiktokMutation = useDisconnectTiktok();

  const isConnected = Boolean(integrationsStatusQuery.data?.tiktokConnected);

  function onSubmit(values: TiktokPixelFormValues) {
    const accessToken = values.eventsApiAccessToken?.trim();
    connectTiktokMutation.mutate({
      pixelId: values.tiktokPixelId.trim(),
      ...(accessToken ? {accessToken} : {}),
    });
  }

  function handleDisconnect() {
    disconnectTiktokMutation.mutate(undefined, {
      onSuccess: () => {
        reset({tiktokPixelId: "", eventsApiAccessToken: ""});
      },
    });
  }

  return (
    <DashboardWrapper pageTitle="TikTok Pixel">
      <StyledPageHeader>Automations</StyledPageHeader>
      <StyledAutomationsPageContent>
        <StyledIntegrationDetailHeader>
          <Breadcrumb>
            <Breadcrumb.Item>
              <Link to="/automations?tab=integrations">Integrations</Link>
            </Breadcrumb.Item>
            <Breadcrumb.Item>TikTok Pixel</Breadcrumb.Item>
          </Breadcrumb>
        </StyledIntegrationDetailHeader>

        <StyledIntegrationDetailCard>
          <StyledIntegrationDetailLayout>
            <IntegrationConnectionVisual integrationIcon={<TiktokIcon />} />
            <form onSubmit={handleSubmit(onSubmit)}>
              <StyledIntegrationConnectPanel>
                <h2>Once you connect, you will be able to track:</h2>
                <ul>
                  {TRACKING_POINTS.map((point) => (
                    <li key={point}>{point}</li>
                  ))}
                </ul>

                <Controller
                  control={control}
                  name="tiktokPixelId"
                  render={({
                    field: {onChange, onBlur, value, ref},
                    fieldState: {error},
                  }) => (
                    <StyledIntegrationField>
                      <label htmlFor="tiktokPixelId">Tiktok pixel ID</label>
                      <Input
                        id="tiktokPixelId"
                        ref={ref}
                        value={value}
                        onChange={onChange}
                        onBlur={onBlur}
                        status={error?.message ? "error" : ""}
                        placeholder="e.g., C8FD2KL7890123456"
                        disabled={isConnected}
                      />
                      <FormFieldError errorMessage={error?.message} />
                      <p className="integration-field-helper">
                        Need help finding your Pixel ID?{" "}
                        <a href={PIXEL_ID_HELP_URL} target="_blank" rel="noreferrer">
                          Learn how
                        </a>
                      </p>
                    </StyledIntegrationField>
                  )}
                />

                <Controller
                  control={control}
                  name="eventsApiAccessToken"
                  render={({
                    field: {onChange, onBlur, value, ref},
                    fieldState: {error},
                  }) => (
                    <StyledIntegrationField>
                      <label htmlFor="eventsApiAccessToken">
                        Events API access token
                      </label>
                      <Input
                        id="eventsApiAccessToken"
                        ref={ref}
                        value={value}
                        onChange={onChange}
                        onBlur={onBlur}
                        status={error?.message ? "error" : ""}
                        placeholder="e.g., a1b2c3d4e5f6g7h8i9j0k1l2m3n4o5p6"
                        disabled={isConnected}
                      />
                      <FormFieldError errorMessage={error?.message} />
                      <p className="integration-field-helper">
                        Events API offers better &amp; effective campaign tracking.{" "}
                        <a href={EVENTS_API_HELP_URL} target="_blank" rel="noreferrer">
                          Learn more
                        </a>
                      </p>
                    </StyledIntegrationField>
                  )}
                />

                {isConnected ? (
                  <StyledIntegrationConnectButton
                    type="default"
                    htmlType="button"
                    onClick={handleDisconnect}
                    loading={disconnectTiktokMutation.isLoading}
                    disabled={disconnectTiktokMutation.isLoading}
                  >
                    Disconnect TikTok Pixel
                  </StyledIntegrationConnectButton>
                ) : (
                  <StyledIntegrationConnectButton
                    type="primary"
                    htmlType="submit"
                    loading={connectTiktokMutation.isLoading}
                    disabled={connectTiktokMutation.isLoading}
                  >
                    Connect Tiktok pixel
                  </StyledIntegrationConnectButton>
                )}
              </StyledIntegrationConnectPanel>
            </form>
          </StyledIntegrationDetailLayout>
        </StyledIntegrationDetailCard>
      </StyledAutomationsPageContent>
    </DashboardWrapper>
  );
}
